import React, { useState, useEffect } from 'react';
import { parseLocalDate, formatDate } from '../utils/dateUtils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { DollarSign, Award, Calendar, ChevronLeft, ChevronRight, FileText, Users, TrendingUp } from 'lucide-react';
import { calculateLoanCommission, getCurrentTier } from '../utils/commissionTiers';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const { isAdmin, isProcessor, user } = useAuth();
  const userBrokerType = user?.broker_type || 'senior';
  const [loans, setLoans] = useState([]);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [loanOfficers, setLoanOfficers] = useState([]);
  const [selectedOfficer, setSelectedOfficer] = useState('mine');

  // When admin views another officer, use that officer's broker type for commission calcs
  const effectiveBrokerType = (() => {
    if (!isAdmin || selectedOfficer === 'mine' || selectedOfficer === 'all') return userBrokerType;
    const officer = loanOfficers.find(o => o.id === selectedOfficer);
    return officer?.broker_type || userBrokerType;
  })();

  useEffect(() => {
    if (isAdmin) {
      const fetchOfficers = async () => {
        try {
          const response = await axios.get(`${API}/api/admin/loan-officers`);
          setLoanOfficers(response.data);
        } catch (error) {
          console.error('Error fetching loan officers:', error);
        }
      };
      fetchOfficers();
    }
  }, [isAdmin]);

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        let url = `${API}/api/loans`;
        if (isAdmin) {
          if (selectedOfficer === 'all') {
            url += '?user_id=all';
          } else if (selectedOfficer !== 'mine') {
            url += `?user_id=${selectedOfficer}`;
          }
        }
        const response = await axios.get(url);
        const transformedLoans = response.data.map(loan => ({
          id: loan.id,
          userId: loan.user_id,
          userName: loan.user_name,
          clientName: loan.client_name,
          loanNumber: loan.loan_number,
          loanAmount: loan.loan_amount,
          baseCommissionRate: loan.base_commission_rate,
          commissionType: loan.commission_type || 'percent',
          lender: loan.lender,
          leadSource: loan.lead_source,
          isSelfGenerated: loan.is_self_generated,
          status: loan.status,
          date: loan.date,
          submissionDate: loan.submission_date,
          reimbursement: loan.reimbursement,
          cure: loan.cure
        }));
        setLoans(transformedLoans);
      } catch (error) {
        console.error('Error fetching loans:', error);
      }
    };
    fetchLoans();
  }, [isAdmin, selectedOfficer]);

  const getMonthKey = (date) => {
    const d = parseLocalDate(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Group loans by month with per-broker breakdown
  const groupLoansByMonth = () => {
    const grouped = {};
    
    loans.forEach(loan => {
      const monthKey = getMonthKey(loan.date);
      if (!grouped[monthKey]) {
        grouped[monthKey] = { monthKey, loans: [], brokers: {} };
      }
      grouped[monthKey].loans.push(loan);
      
      const broker = loan.userName || 'Unknown';
      if (!grouped[monthKey].brokers[broker]) {
        grouped[monthKey].brokers[broker] = { submissions: [], closings: [] };
      }
      
      if (loan.submissionDate) {
        const subMonthKey = getMonthKey(loan.submissionDate);
        if (subMonthKey === monthKey) {
          grouped[monthKey].brokers[broker].submissions.push(loan);
        }
      }
      if (loan.status === 'Closed') {
        grouped[monthKey].brokers[broker].closings.push(loan);
      }
    });

    // Also add submissions that fall in a different month than closing date
    loans.forEach(loan => {
      if (loan.submissionDate) {
        const subMonthKey = getMonthKey(loan.submissionDate);
        const closeMonthKey = getMonthKey(loan.date);
        if (subMonthKey !== closeMonthKey) {
          if (!grouped[subMonthKey]) {
            grouped[subMonthKey] = { monthKey: subMonthKey, loans: [], brokers: {} };
          }
          const broker = loan.userName || 'Unknown';
          if (!grouped[subMonthKey].brokers[broker]) {
            grouped[subMonthKey].brokers[broker] = { submissions: [], closings: [] };
          }
          // Only add submission if not already counted
          if (!grouped[subMonthKey].brokers[broker].submissions.find(l => l.id === loan.id)) {
            grouped[subMonthKey].brokers[broker].submissions.push(loan);
          }
          if (!grouped[subMonthKey].loans.find(l => l.id === loan.id)) {
            grouped[subMonthKey].loans.push(loan);
          }
        }
      }
    });

    // Calculate totals per month
    Object.values(grouped).forEach(month => {
      const allClosedInMonth = month.loans.filter(l => l.status === 'Closed' && getMonthKey(l.date) === month.monthKey);
      const closedCount = allClosedInMonth.length;
      
      month.totalSubmissions = Object.values(month.brokers).reduce((sum, b) => sum + b.submissions.length, 0);
      month.totalClosings = allClosedInMonth.length;
      month.submissionVolume = Object.values(month.brokers).reduce((sum, b) => sum + b.submissions.reduce((s, l) => s + l.loanAmount, 0), 0);
      month.closingVolume = allClosedInMonth.reduce((sum, l) => sum + l.loanAmount, 0);
      
      if (!isProcessor) {
        month.totalCommission = allClosedInMonth.reduce((sum, loan) => {
          const baseCommission = calculateLoanCommission(loan, closedCount, effectiveBrokerType);
          const reimbursement = parseFloat(loan.reimbursement || 0);
          const cure = parseFloat(loan.cure || 0);
          return sum + baseCommission + reimbursement - cure;
        }, 0);
        month.tier = getCurrentTier(closedCount, effectiveBrokerType);
      }
    });

    return Object.values(grouped).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  };

  const monthlyData = groupLoansByMonth();

  const overallStats = {
    totalEarnings: monthlyData.reduce((sum, m) => sum + (m.totalCommission || 0), 0),
    totalClosed: loans.filter(l => l.status === 'Closed').length,
    totalVolume: loans.filter(l => l.status === 'Closed').reduce((sum, l) => sum + l.loanAmount, 0),
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonth(expandedMonth === monthKey ? null : monthKey);
  };

  return (
    <div className="space-y-6">
      {/* Admin Selector */}
      {isAdmin && (
        <Card className="bg-[#111] border border-[#222]">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-teal-400" />
                <span className="text-white font-medium">Viewing:</span>
                <select
                  value={selectedOfficer}
                  onChange={(e) => setSelectedOfficer(e.target.value)}
                  className="bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-2 text-white focus:border-teal-500 focus:outline-none min-w-[200px]"
                >
                  <option value="mine">My Loans Only</option>
                  <option value="all">Company Wide</option>
                  <optgroup label="Individual Officers">
                    {loanOfficers.map(officer => (
                      <option key={officer.id} value={officer.id}>{officer.name}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              {selectedOfficer === 'all' && <Badge className="bg-teal-900/50 text-teal-400 border border-teal-700">Company Wide</Badge>}
              {selectedOfficer !== 'mine' && selectedOfficer !== 'all' && (
                <Badge className="bg-purple-900/50 text-purple-400 border border-purple-700">
                  Viewing: {loanOfficers.find(o => o.id === selectedOfficer)?.name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">{isProcessor ? 'Loan Reports' : 'Commission Reports'}</h1>
        <p className="text-gray-400 text-sm mt-1">{isProcessor ? 'Breakdown of loan performance by month' : 'Monthly performance breakdown with per-broker detail'}</p>
      </div>

      {loans.length > 0 ? (
        <>
          {/* Summary Stats */}
          <div className={`grid grid-cols-1 ${isProcessor ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
            {!isProcessor && (
              <Card className="bg-[#111] border border-[#222]">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-gray-400 text-xs uppercase tracking-wide">Total Earnings</p>
                    <DollarSign className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold text-green-400">
                    ${overallStats.totalEarnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </CardContent>
              </Card>
            )}
            <Card className="bg-[#111] border border-[#222]">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-gray-400 text-xs uppercase tracking-wide">Total Closed</p>
                  <Award className="w-4 h-4 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-blue-400">{overallStats.totalClosed}</p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </CardContent>
            </Card>
            <Card className="bg-[#111] border border-[#222]">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-gray-400 text-xs uppercase tracking-wide">Total Volume</p>
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                </div>
                <p className="text-2xl font-bold text-emerald-400">${overallStats.totalVolume.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Closed loans</p>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Breakdown - Column Headers */}
          <div className="px-6 py-3 flex items-center">
            <div className="flex-1" />
            <div className="flex items-center">
              <div className="w-24 text-right">
                <p className="text-[10px] text-gray-600 uppercase tracking-wider font-semibold">Submissions</p>
              </div>
              <div className="w-20 text-right">
                <p className="text-[10px] text-gray-600 uppercase tracking-wider font-semibold">Closings</p>
              </div>
              <div className="w-28 text-right">
                <p className="text-[10px] text-gray-600 uppercase tracking-wider font-semibold">Volume</p>
              </div>
              {!isProcessor && (
                <div className="w-28 text-right">
                  <p className="text-[10px] text-gray-600 uppercase tracking-wider font-semibold">Commission</p>
                </div>
              )}
              <div className="w-20" />
              <div className="w-6" />
            </div>
          </div>

          <div className="space-y-3">
            {monthlyData.map((data) => (
              <Card key={data.monthKey} className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
                {/* Month Header - Clickable */}
                <button
                  onClick={() => toggleMonth(data.monthKey)}
                  className="w-full px-6 py-5 flex items-center hover:bg-[#1a1a1a] transition-colors"
                  data-testid={`month-row-${data.monthKey}`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-9 h-9 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-gray-400" />
                    </div>
                    <span className="text-white font-bold text-lg">{formatMonth(data.monthKey)}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-24 text-right">
                      <p className="text-blue-400 font-bold text-base">{data.totalSubmissions}</p>
                    </div>
                    <div className="w-20 text-right">
                      <p className="text-white font-bold text-base">{data.totalClosings}</p>
                    </div>
                    <div className="w-28 text-right">
                      <p className="text-emerald-400 font-semibold text-base">${data.closingVolume.toLocaleString()}</p>
                    </div>
                    {!isProcessor && (
                      <div className="w-28 text-right">
                        <p className="text-green-400 font-semibold text-base">${(data.totalCommission || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    )}
                    {!isProcessor && data.tier ? (
                      <div className="w-20 flex justify-center">
                        <Badge className="bg-[#1a1a1a] text-gray-300 border border-[#333] text-xs">{data.tier.name}</Badge>
                      </div>
                    ) : (
                      <div className="w-20" />
                    )}
                    <div className="w-6 flex justify-center">
                      <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${expandedMonth === data.monthKey ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                </button>

                {/* Expanded: Per-Broker Table */}
                {expandedMonth === data.monthKey && (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid={`broker-table-${data.monthKey}`}>
                      <thead>
                        <tr className="border-b border-[#222]">
                          <th className="text-left py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider">Broker</th>
                          <th className="text-center py-3 px-4 text-gray-400 text-xs font-semibold uppercase tracking-wider">Submissions</th>
                          <th className="text-right py-3 px-4 text-gray-400 text-xs font-semibold uppercase tracking-wider">Sub Volume</th>
                          <th className="text-center py-3 px-4 text-gray-400 text-xs font-semibold uppercase tracking-wider">Closings</th>
                          <th className="text-right py-3 px-4 text-gray-400 text-xs font-semibold uppercase tracking-wider">Close Volume</th>
                          {!isProcessor && (
                            <th className="text-right py-3 px-6 text-gray-400 text-xs font-semibold uppercase tracking-wider">Commission</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(data.brokers)
                          .sort((a, b) => b[1].closings.reduce((s, l) => s + l.loanAmount, 0) - a[1].closings.reduce((s, l) => s + l.loanAmount, 0))
                          .map(([broker, stats], idx) => {
                            const subVolume = stats.submissions.reduce((s, l) => s + l.loanAmount, 0);
                            const closeVolume = stats.closings.reduce((s, l) => s + l.loanAmount, 0);
                            const commission = !isProcessor ? stats.closings.reduce((sum, loan) => {
                              const base = calculateLoanCommission(loan, data.totalClosings, effectiveBrokerType);
                              return sum + base + parseFloat(loan.reimbursement || 0) - parseFloat(loan.cure || 0);
                            }, 0) : 0;
                            const hasClosings = stats.closings.length > 0;
                            
                            return (
                              <tr key={broker} className={`border-b border-[#222] hover:bg-[#1a1a1a] transition-colors ${
                                hasClosings ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-transparent'
                              }`}>
                                <td className="py-3.5 px-6 text-white font-medium">{broker}</td>
                                <td className="py-3.5 px-4 text-center">
                                  <span className="text-blue-400 font-semibold">{stats.submissions.length}</span>
                                </td>
                                <td className="py-3.5 px-4 text-right text-gray-300">${subVolume.toLocaleString()}</td>
                                <td className="py-3.5 px-4 text-center">
                                  <span className={`font-bold text-lg ${hasClosings ? 'text-green-400' : 'text-gray-600'}`}>{stats.closings.length}</span>
                                </td>
                                <td className="py-3.5 px-4 text-right">
                                  <span className={hasClosings ? 'text-green-400 font-semibold' : 'text-gray-600'}>${closeVolume.toLocaleString()}</span>
                                </td>
                                {!isProcessor && (
                                  <td className="py-3.5 px-6 text-right">
                                    <span className={hasClosings ? 'text-green-400 font-semibold' : 'text-gray-600'}>
                                      ${commission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-[#1a1a1a] border-t border-[#333]">
                          <td className="py-3.5 px-6 text-white font-bold">Total</td>
                          <td className="py-3.5 px-4 text-center text-white font-bold">{data.totalSubmissions}</td>
                          <td className="py-3.5 px-4 text-right text-white font-bold">${data.submissionVolume.toLocaleString()}</td>
                          <td className="py-3.5 px-4 text-center text-white font-bold">{data.totalClosings}</td>
                          <td className="py-3.5 px-4 text-right text-green-400 font-bold">${data.closingVolume.toLocaleString()}</td>
                          {!isProcessor && (
                            <td className="py-3.5 px-6 text-right text-green-400 font-bold">
                              ${(data.totalCommission || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      ) : (
        <Card className="bg-[#111] border border-[#222]">
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <Calendar className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Loans Yet</h3>
              <p className="text-gray-500">Start adding loans in the Loan Management tab to see your reports here</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
